import React from 'react';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import { isType } from '../../../../utils/react-children-utils/react-children-utils';
import { checkHTMLBool, getValidationMessage } from '../../../../utils/form-utils';

/// This is a wrapper around an <input> that enables show/hide for passwords
class InputContainer extends React.Component {
    constructor(props) {
        super(props);

        this.validate = this.validate.bind(this);
        this.passerror = this.passerror.bind(this);
        this.getInputs = this.getInputs.bind(this);

        // if validation has not been passed down valid should always be true;
        this.state = {
            error: '',
        };
    }

    getInputs() {
        const { formError } = this.props;
        let { children } = this.props;

        // we do not want to flatten here, otherwise we'll get both container and input.
        children = React.Children.toArray(children);

        const { error } = this.state;
        const input = children.find((c) => isType(c, 'input'));
        const specialControls = children.filter((c) => !isType(c, 'input'));

        // If we have been passed a validate(value) function then
        // try and get the message elements.
        let validationMessage = children.find((c) => getValidationMessage(c));
        if (validationMessage) {
            validationMessage = React.cloneElement(
                validationMessage,
                { children: error || formError },
            );
        }

        if (input && specialControls.length <= 1) {
            const {
                defaultValue,
                readOnly,
                onChange,
                value,
                name,
                type,
                disabled,
                placeholder,
                maxlength,
            } = input.props;

            let newInput = (
                <input
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    className={`${input.props.className} is-white ${error || formError ? styles.invalid : ''}`}
                    onInput={this.validate}
                    maxLength={maxlength}
                    data-testid={`${name}-input`}
                    onWheel={(event) => { event.preventDefault(); }}
                />
            );

            if (value) {
                newInput = <input {...newInput.props} value={value} />;
            } else {
                newInput = <input {...newInput.props} defaultValue={value ?? defaultValue} />;
            }

            if (checkHTMLBool(readOnly)) {
                newInput = <input {...newInput.props} readOnly />;
            }

            if (checkHTMLBool(disabled)) {
                newInput = <input {...newInput.props} disabled />;
            }

            if (onChange) {
                newInput = (
                    <input
                        {...newInput.props}
                        onChange={(event) => {
                            onChange(event);
                            this.validate(event);
                        }}
                    />
                );
            }

            return (
                <>
                    {newInput}
                    {validationMessage}
                </>
            );
        }

        const specialInput = children.find((c) => !getValidationMessage(c));

        const newInput = React.cloneElement(
            specialInput,
            {
                validation: this.validate,
                passerror: this.passerror,
                invalidclass: error || formError ? styles.invalid : '',
            },
        );

        return (
            <>
                {newInput}
                {validationMessage}
            </>
        );
    }

    validate(event, error) {
        const {
            passerror,
            required,
            name,
            displayname,
            validation,
        } = this.props;

        let newError = error;
        if (required && checkHTMLBool(required)) {
            const input = event.target;
            const { value, type, checked } = input;

            const valueToCheck = type === 'checkbox' ? checked : value;

            // We want to prioritise the if empty validation rule.
            // We don't want "value is invalid" error message if there's nothing there
            if (!valueToCheck) {
                newError = `${displayname} is required`;
            }
        }

        if (validation) {
            validation(event, newError);
        }

        // Callback to tell the form that this control is valid/invalid
        this.setState(() => ({
            error: newError,
        }), () => passerror(name, newError));
    }

    passerror(error) {
        const { passerror, name } = this.props;

        // Callback to tell the form that this control is valid/invalid
        this.setState(() => ({
            error,
        }), () => passerror(name, error));
    }

    render() {
        const newInput = this.getInputs();

        return (
            <div className={`control ${styles.InputContainer}`}>
                {newInput}
            </div>
        );
    }
}

InputContainer.propTypes = {
    children: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
    displayname: PropTypes.string,
    passerror: PropTypes.func,
    formError: PropTypes.string,
    required: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    validation: PropTypes.func,
};

InputContainer.defaultProps = {
    passerror: () => { },
    displayname: '',
    formError: '',
    required: false,
    validation: () => { },
};

export default InputContainer;
